import { API_BASE_URL } from '../config/api'
import type {
  LedgerSafeEditorialEvent,
  LedgerSafeEditorialRowSnapshot,
} from '../types/ledgerSafeEditorialEvents'

export type LedgerSafePostgresTrialOperationKind =
  | 'relocate_cells'
  | 'boundary_shift'
  | 'merge_units'

export interface LedgerSafePostgresTrialEditRequest {
  chapterId: string
  operationId: string
  expectedRevision: number
  kind: LedgerSafePostgresTrialOperationKind
  rowUpserts: LedgerSafeEditorialRowSnapshot[]
  rowDeletes: string[]
  editorialEvent: LedgerSafeEditorialEvent
}

export interface LedgerSafePostgresTrialHead {
  revision: number
}

export interface LedgerSafePostgresTrialEditReceipt {
  revision: number
  duplicate: boolean
  requestBytes: number
  serverTransactionMs?: number
}

export type LedgerSafePostgresTrialAPIErrorCode =
  | 'aborted'
  | 'http'
  | 'invalid_response'
  | 'network'
  | 'timeout'

export interface LedgerSafePostgresTrialAPIError {
  code: LedgerSafePostgresTrialAPIErrorCode
  message: string
  status: number | null
  serverCode?: string
}

export type LedgerSafePostgresTrialAPIResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: LedgerSafePostgresTrialAPIError }

export interface LedgerSafePostgresTrialRequestOptions {
  fetchImpl?: typeof fetch
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 5_000
const MAX_ERROR_MESSAGE_LENGTH = 240

const boundedMessage = (value: unknown, fallback: string): string => {
  const message = value instanceof Error
    ? value.message
    : typeof value === 'string'
      ? value
      : fallback
  const normalized = message.trim() || fallback
  return normalized.length > MAX_ERROR_MESSAGE_LENGTH
    ? `${normalized.slice(0, MAX_ERROR_MESSAGE_LENGTH - 1)}…`
    : normalized
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

const parseRevision = (payload: unknown): number | null => {
  if (!isRecord(payload)) {
    return null
  }
  const sources = [
    payload,
    isRecord(payload.head) ? payload.head : null,
  ].filter((source): source is Record<string, unknown> => Boolean(source))
  for (const source of sources) {
    const candidate = source.revision ??
      source.appliedRevision ??
      source.applied_revision ??
      source.headRevision ??
      source.head_revision
    if (typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate >= 0) {
      return candidate
    }
  }
  return null
}

const parseDuplicate = (payload: unknown): boolean =>
  isRecord(payload) && (
    payload.duplicate === true ||
    payload.idempotent === true ||
    payload.alreadyApplied === true ||
    payload.already_applied === true ||
    payload.idempotentReplay === true ||
    payload.idempotent_replay === true
  )

const parseServerTransactionMs = (payload: unknown): number | null => {
  if (!isRecord(payload)) {
    return null
  }
  const candidate = payload.serverTransactionMs ?? payload.server_transaction_ms
  return typeof candidate === 'number' &&
    Number.isFinite(candidate) &&
    candidate >= 0
    ? Math.min(120_000, candidate)
    : null
}

const serializedByteLength = (value: string): number => {
  try {
    if (typeof TextEncoder === 'function') {
      return new TextEncoder().encode(value).byteLength
    }
  } catch {
    // The UTF-16 length remains a bounded fallback for development telemetry.
  }
  return value.length
}

const readBoundedErrorDetail = async (
  response: Response,
): Promise<{ message: string; serverCode?: string }> => {
  const raw = await response.text().catch(() => '')
  if (!raw) {
    return { message: `HTTP ${response.status}` }
  }
  try {
    const payload = JSON.parse(raw) as { code?: unknown; detail?: unknown; message?: unknown }
    const detailRecord = isRecord(payload.detail) ? payload.detail : null
    let detail = raw
    if (typeof payload.detail === 'string') {
      detail = payload.detail
    } else if (typeof detailRecord?.message === 'string') {
      detail = detailRecord.message
    } else if (typeof payload.message === 'string') {
      detail = payload.message
    }
    const serverCodeCandidate = typeof detailRecord?.code === 'string'
      ? detailRecord.code
      : typeof payload.code === 'string'
        ? payload.code
        : null
    return {
      message: boundedMessage(detail, `HTTP ${response.status}`),
      ...(serverCodeCandidate
        ? { serverCode: serverCodeCandidate.slice(0, 80) }
        : {}),
    }
  } catch {
    return { message: boundedMessage(raw, `HTTP ${response.status}`) }
  }
}

const requestJson = async (
  url: string,
  init: RequestInit,
  options: LedgerSafePostgresTrialRequestOptions,
): Promise<
  | { ok: true; response: Response; payload: unknown }
  | { ok: false; error: LedgerSafePostgresTrialAPIError }
> => {
  const fetchImpl = options.fetchImpl || globalThis.fetch
  if (typeof fetchImpl !== 'function') {
    return {
      ok: false,
      error: {
        code: 'network',
        message: 'Fetch is unavailable.',
        status: null,
      },
    }
  }

  const controller = new AbortController()
  const timeoutMs = Math.max(1, options.timeoutMs ?? DEFAULT_TIMEOUT_MS)
  const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetchImpl(url, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    })
    if (!response.ok) {
      const detail = await readBoundedErrorDetail(response)
      return {
        ok: false,
        error: {
          code: 'http',
          message: detail.message,
          status: response.status,
          ...(detail.serverCode ? { serverCode: detail.serverCode } : {}),
        },
      }
    }
    try {
      return { ok: true, response, payload: await response.json() }
    } catch {
      return {
        ok: false,
        error: {
          code: 'invalid_response',
          message: 'PostgreSQL trial returned invalid JSON.',
          status: response.status,
        },
      }
    }
  } catch (error) {
    const aborted = controller.signal.aborted
    return {
      ok: false,
      error: {
        code: aborted ? 'timeout' : 'network',
        message: aborted
          ? `PostgreSQL trial request timed out after ${timeoutMs} ms.`
          : boundedMessage(error, 'PostgreSQL trial request failed.'),
        status: null,
      },
    }
  } finally {
    globalThis.clearTimeout(timeoutId)
  }
}

const trialPath = (trialId: string): string =>
  `${API_BASE_URL}/api/workbench/ledger-safe-postgres-trials/${encodeURIComponent(trialId)}`

export const getLedgerSafePostgresTrialHead = async (
  trialId: string,
  chapterId: string,
  options: LedgerSafePostgresTrialRequestOptions = {},
): Promise<LedgerSafePostgresTrialAPIResult<LedgerSafePostgresTrialHead>> => {
  const result = await requestJson(
    `${trialPath(trialId)}/chapters/${encodeURIComponent(chapterId)}/head`,
    { method: 'GET' },
    options,
  )
  if (!result.ok) {
    return result
  }
  const revision = parseRevision(result.payload)
  if (revision === null) {
    return {
      ok: false,
      error: {
        code: 'invalid_response',
        message: 'PostgreSQL trial head did not include a valid revision.',
        status: result.response.status,
      },
    }
  }
  return { ok: true, value: { revision } }
}

export const postLedgerSafePostgresTrialEdit = async (
  trialId: string,
  request: LedgerSafePostgresTrialEditRequest,
  options: LedgerSafePostgresTrialRequestOptions = {},
): Promise<LedgerSafePostgresTrialAPIResult<LedgerSafePostgresTrialEditReceipt>> => {
  const serializedRequest = JSON.stringify(request)
  const requestBytes = serializedByteLength(serializedRequest)
  const result = await requestJson(
    `${trialPath(trialId)}/edits`,
    {
      method: 'POST',
      body: serializedRequest,
    },
    options,
  )
  if (!result.ok) {
    return result
  }
  const revision = parseRevision(result.payload)
  if (revision === null) {
    return {
      ok: false,
      error: {
        code: 'invalid_response',
        message: 'PostgreSQL trial edit receipt did not include a valid revision.',
        status: result.response.status,
      },
    }
  }
  const serverTransactionMs = parseServerTransactionMs(result.payload)
  return {
    ok: true,
    value: {
      revision,
      duplicate: parseDuplicate(result.payload),
      requestBytes,
      ...(serverTransactionMs !== null
        ? { serverTransactionMs }
        : {}),
    },
  }
}
